<template>
  <div class="flex flex-col gap-2">
    <div class="flex gap-6 items-start flex-wrap">
      <template v-if="mode === 'view'">
        <div>
          <ViewModeLabel>Task Name</ViewModeLabel>
          <ViewModeValue>{{ taskName }}</ViewModeValue>
        </div>
      </template>
      <template v-else>
        <div>
          <FormKit
            v-model="taskName"
            label="Task Name"
            name="task_name"
            type="text"
            outer-class="w-56 max-w-56 !m-0"
            placeholder="Follow up with candidate"
          />
        </div>
      </template>

      <UserSelector
        v-model:selected-type="recipientType"
        v-model:selected-user-ids="recipientUserIds"
        label="Assignee"
        name="recipient_type"
        :type-options="recipientTypeOptions"
        show-users-for-type="account_users"
        :mode="mode"
      />

      <template v-if="mode === 'view'">
        <div>
          <ViewModeLabel>Due Date</ViewModeLabel>
          <ViewModeValue>{{ dueDateDisplay }}</ViewModeValue>
        </div>
      </template>
      <template v-else>
        <div>
          <FormKit
            v-model="taskDescription"
            label="Description (optional)"
            name="task_description"
            type="textarea"
            outer-class="w-64 max-w-64 !m-0"
            rows="3"
          />
        </div>

        <div>
          <FormKit
            v-model="dueDateOption"
            label="Due Date"
            name="due_date_offset"
            type="select"
            :options="dueDateOptions"
            outer-class="w-44 max-w-44 !m-0"
          />
        </div>
      </template>
    </div>

    <Accordion v-if="mode === 'view' && taskDescription" type="single" collapsible class="w-full">
      <AccordionItem value="details" class="border-none">
        <AccordionTrigger class="py-1 text-sm text-muted-foreground hover:no-underline">
          <span class="flex items-center gap-1">
            <vue-feather type="file-text" class="w-3 h-3" />
            View description
          </span>
        </AccordionTrigger>
        <AccordionContent class="pt-2">
          <div class="bg-muted/50 rounded-md p-3 text-sm whitespace-pre-wrap">
            {{ taskDescription }}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { ActionableProps } from '../types';
import UserSelector from './common/UserSelector.vue';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type {
  WorkflowActionableOneOffTaskParams,
  WorkflowActionRecipientType,
  WorkflowActionOneOffTaskEntity,
} from '@/api/entity_types/workflow/WorkflowAction';
import ViewModeLabel from '../../common/ViewModeLabel.vue';
import ViewModeValue from '../../common/ViewModeValue.vue';

const { actionable: taskActionable, mode } = defineProps<ActionableProps<WorkflowActionOneOffTaskEntity>>();

const recipientType = ref<WorkflowActionRecipientType>('account_users');
const recipientUserIds = ref<number[]>([]);
const taskName = ref<string>('');
const taskDescription = ref<string>('');
const dueDateOption = ref<string>('0');

const recipientTypeOptions = [
  { label: 'Account Users', value: 'account_users' },
  { label: 'Candidate', value: 'candidate' },
];

const dueDateOptions = [
  { label: 'None', value: undefined },
  { label: '24 hours after', value: '24' },
  { label: '48 hours after', value: '48' },
  { label: '72 hours after', value: '72' },
];

const dueDateDisplay = computed(() => {
  const hours = parseInt(dueDateOption.value, 10);
  if (hours === 0) return 'None';
  return `${hours} hours after`;
});

const initSelectedOption = (taskActionable?: WorkflowActionOneOffTaskEntity) => {
  recipientType.value = taskActionable?.recipient_type || 'account_users';
  recipientUserIds.value = taskActionable?.recipient_user_ids || [];
  taskName.value = taskActionable?.task_name || '';
  taskDescription.value = taskActionable?.task_description || '';
  dueDateOption.value = String(taskActionable?.due_date_offset_hours ?? 0);
};

const buildActionableParams = () => {
  const params: WorkflowActionableOneOffTaskParams = {
    actionable_type: 'WorkflowActionOneOffTask',
    actionable: {
      recipient_type: recipientType.value,
      recipient_user_ids: recipientUserIds.value,
      task_name: taskName.value,
      task_description: taskDescription.value || undefined,
      due_date_offset_hours: parseInt(dueDateOption.value, 10),
    },
  };
  return params;
};

const resetFields = (taskActionable: WorkflowActionOneOffTaskEntity) => {
  initSelectedOption(taskActionable);
};

defineExpose({
  buildActionableParams,
  resetFields,
});

initSelectedOption(taskActionable);
</script>
